import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';

/** Focus-contained dialog; inline template editing intentionally stays in this dialog. */
export function InvitationDialog({ title, children, onClose, busy = false }: {
  title: string; children: ReactNode; onClose: () => void; busy?: boolean;
}) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => { document.body.style.overflow = overflow; previous?.focus(); };
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 backdrop-blur-sm sm:p-6">
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
        className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl outline-none"
        onKeyDown={(event) => {
          if (event.key === 'Escape') { event.stopPropagation(); if (!busy) close.current(); }
          if (event.key !== 'Tab') return;
          const nodes = Array.from(panel.current?.querySelectorAll<HTMLElement>(
            'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, iframe, [tabindex="0"]',
          ) ?? []).filter(node => {
            if (node.closest('[hidden]') || node.matches(':disabled') || node.tabIndex < 0) return false;
            let ancestor = node.parentElement;
            while (ancestor && ancestor !== panel.current) {
              if (ancestor instanceof HTMLDetailsElement && !ancestor.open && !ancestor.querySelector('summary')?.contains(node)) return false;
              ancestor = ancestor.parentElement;
            }
            return true;
          });
          const first = nodes[0]; const last = nodes[nodes.length - 1];
          if (!first) { event.preventDefault(); return; }
          if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
            event.preventDefault(); last.focus();
          } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel.current)) {
            event.preventDefault(); first.focus();
          }
        }}>
        <header className="flex items-center justify-between gap-4 border-b bg-muted/30 px-5 py-4">
          <h2 id={titleId} className="text-xl font-semibold">{title}</h2>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy} aria-label={`Close ${title}`}>Close</Button>
        </header>
        <div className="overflow-y-auto p-5">{children}</div>
      </div>
    </div>, document.body,
  );
}